'use client';

import { memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type GalleryGridProps = {
  /** Grid items */
  children: ReactNode;
  /** Number of columns (default: 4). Use 'auto' for responsive auto-fit with minItemWidth. */
  columns?: 2 | 3 | 4 | 'auto';
  /** Gap between items in pixels (default: 12) */
  gap?: 8 | 12 | 16;
  /** Minimum item width in pixels when columns='auto' (default: 100) */
  minItemWidth?: number;
  /** Section title */
  title?: string;
  /** Item count to display next to title */
  count?: number;
  /** Stable id for the section heading (pairs with aria-labelledby on a parent section) */
  headingId?: string;
};

const COLUMN_CLASSES = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
} as const;

const GAP_CLASSES = {
  8: 'gap-2',
  12: 'gap-3',
  16: 'gap-4',
} as const;

/**
 * Grid container for gallery items.
 * Supports responsive column counts and configurable gaps.
 * Use columns='auto' with minItemWidth for truly responsive grids.
 */
export const GalleryGrid = memo(
  ({ children, columns = 4, gap = 12, headingId, minItemWidth = 100, title, count }: GalleryGridProps) => {
    const gridClassName = columns === 'auto' ? GAP_CLASSES[gap] : cn(COLUMN_CLASSES[columns], GAP_CLASSES[gap]);

    const gridStyle =
      columns === 'auto' ? { gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}px, 1fr))` } : undefined;

    return (
      <div>
        {title && (
          <h3 className="mb-3 font-bold text-10 text-neutral-10 uppercase tracking-wide" id={headingId}>
            {count !== undefined && `${count} `}
            {title}
          </h3>
        )}
        <div className={cn('grid', gridClassName)} style={gridStyle}>
          {children}
        </div>
      </div>
    );
  },
);

GalleryGrid.displayName = 'GalleryGrid';
